/**
 * Domain types for the native Coral Placer (catalog, interactive guide,
 * placement canvas). Pure data — no React, no I/O — so it can be shared
 * by the UI components and unit-tested in isolation.
 */

/** The seven color buckets a coral can be tagged with. */
export const COLOR_TAGS = [
  'YELLOW',
  'WARM',
  'BLUE',
  'PURPLE',
  'GREEN',
  'PINK',
  'MULTI',
] as const;
export type ColorTag = (typeof COLOR_TAGS)[number];

/** The five Acropora growth forms used to group corals. */
export const GROWTH_FORMS = [
  'STAG',
  'TABLING',
  'CORYMBOSE',
  'BUSHY',
  'BOTTLEBRUSH',
] as const;
export type GrowthForm = (typeof GROWTH_FORMS)[number];

/** A fully-normalized catalog coral (after merging any saved edits). */
export interface Coral {
  readonly id: string;
  readonly name: string;
  readonly species: string;
  readonly growthFormRaw: string;
  readonly bodyColor: string;
  readonly polypColor: string;
  /** Bare filename inside `/coral-placer/photos/frags/`. */
  readonly fragPhoto: string;
  /** Bare filename inside `/coral-placer/photos/colonies/`. */
  readonly colonyPhoto: string;
  readonly tsaUrl: string;
  readonly colors: readonly ColorTag[];
  readonly growthForm: GrowthForm;
}

/**
 * Operator-supplied edits to a catalog coral, persisted to the Pi as an
 * overlay keyed by coral id. Only the fields the operator changed are
 * stored; everything else falls through to the base catalog entry.
 */
export interface CoralEdit {
  readonly name?: string;
  readonly species?: string;
  readonly colors?: readonly ColorTag[];
  readonly growthForm?: GrowthForm;
  readonly bodyColor?: string;
  readonly polypColor?: string;
  readonly tsaUrl?: string;
}

/** Map of coral id → operator edits. */
export type CoralEdits = Record<string, CoralEdit>;

/** Which catalog photo a placement dot displays. */
export type PlacementPhoto = 'frag' | 'colony';

/** A single coral placed on an aquascape photo. */
export interface Placement {
  readonly id: string;
  readonly coralId: string;
  /** Horizontal position as a fraction (0–1) of the image width. */
  readonly xPct: number;
  /** Vertical position as a fraction (0–1) of the image height. */
  readonly yPct: number;
  /** Dot diameter as a fraction (0–1) of the image width. */
  readonly sizePct: number;
  /** Which catalog photo to show inside the dot. */
  readonly photo: PlacementPhoto;
}

/**
 * An aquascape backdrop. `src` is either a static path (the bundled
 * sample) or a self-contained data URL (an operator upload, so it
 * survives being saved to the Pi without a separate image store).
 */
export interface Aquascape {
  readonly id: string;
  readonly name: string;
  readonly src: string;
  /** True for the bundled sample shot (cannot be deleted). */
  readonly builtIn?: boolean;
}

/** The full persisted Coral-Placer workspace (saved to the Pi). */
export interface Workspace {
  readonly version: 1;
  readonly aquascapes: readonly Aquascape[];
  readonly activeAquascapeId: string;
  /** Placements keyed by aquascape id. */
  readonly placements: Record<string, readonly Placement[]>;
  /** Default dot diameter (fraction of image width) for new placements. */
  readonly defaultDotSize: number;
}

/** The three top-level views in the Coral Placer page. */
export type CoralPlacerView = 'guide' | 'placer' | 'collection';

/** The three layouts of the Interactive Guide. */
export type GuideLayout = 'cards' | 'list' | 'mindmap';

/** Catalog sort options. */
export type SortKey = 'name-asc' | 'name-desc' | 'growth' | 'color';

/** The active filter state shared across views. */
export interface FilterState {
  readonly query: string;
  readonly colors: readonly ColorTag[];
  readonly growthForms: readonly GrowthForm[];
  readonly sort: SortKey;
}
